import { Fragment, type ReactElement, type ReactNode } from 'react';

import { ToolbarButton } from './ToolbarButton';
import styles from './Toolbar.module.css';
import type { ToolbarItem } from './types';

/**
 * The action bar — docs/DESIGN.md §6.2, §8, ADR-0014, recon §1.2.
 *
 * It maps data to buttons and knows nothing else. It has never heard of JSON, of a document,
 * or of what any of these actions do: a `ToolbarItem` arrives with its label already
 * translated, its availability already decided and its effect already closed over. That is
 * the whole content of "one shell for two seams" — adding Fix all, Expand all or Compare is
 * an entry in `src/features/registry.ts` and nothing here (ADR-0014, ADR-0020 §8.3).
 *
 * ═══ UNAVAILABLE MEANS ABSENT, NOT DISABLED ═══
 *
 * `available: false` renders nothing at all. The case that forces it is Paste in Firefox,
 * where `navigator.clipboard.readText` is not exposed to page scripts at any permission
 * level: a disabled Paste button there is a promise the browser will never let the page keep,
 * and the legacy build's answer — show it, then fail at click time with a generic error —
 * reads to the user as a broken product rather than as a browser policy. `disabled: true` is
 * for the other thing: an action that is momentarily inapplicable (Format on an empty
 * buffer), which is worth showing so the user learns it exists.
 *
 * ═══ THE BAR DOES NOT SCROLL, EVER, AND NO LABEL IS SHED ═══
 *
 * All three rejected design directions hid 235px, 249px and 76px of toolbar behind a
 * horizontal scroller on a phone, and ADR-0100's answer — shed the labels for icons below
 * 900px and 640px — is itself overturned by ADR-0101: the wand read as a pencil, and an
 * icon-only Clear is a guess with a destructive answer. Every action keeps its word at every
 * width. A word may be SHORTER below 960px (`compactLabel`: "Example" for "Load example"),
 * and the accessible name is the full label either way.
 *
 * ═══ THE ROW BREAK IS A SEPARATOR, NOT A COUNT ═══
 *
 * Below 760px the bar is the phone dock and has to break after the transforms (ADR-0101 §7.6).
 * The break is expressed by marking the FIRST group separator `data-row-break`; the stylesheet
 * widens that one hairline to the full row and a flex row must wrap after it. Nothing here
 * counts buttons, and nothing names a group: regroup `features/registry.ts` and the dock
 * re-breaks itself. The attribute — not a class — because CSS Modules hashes class names and
 * this one has to be selectable as a state.
 */

export interface ToolbarProps {
  readonly items: readonly ToolbarItem[];
  /** Localised, `a11y.toolbar`. Names the toolbar landmark. */
  readonly label: string;
  /**
   * The document tag — `23 lines · 412 B`, already formatted and localised
   * (`editor.documentTag`). It sits between the spacer and the trailing run, and it is drawn
   * only from 960px: below that the bar is the phone dock, where every pixel belongs to an
   * action (ADR-0101 §6.2, §7.6).
   */
  readonly documentTag?: ReactNode;
  readonly className?: string;
}

function renderRun(items: readonly ToolbarItem[], marksRowBreak: boolean): ReactNode[] {
  // The dock breaks after the first group, and only there. A `let` rather than an index
  // comparison because "the first separator" is not "the separator before item 3": which item
  // starts the second group is the registry's business, not this loop's.
  let rowBreakTaken = false;

  return items.map((item, index) => {
    const previous = items[index - 1];
    // A structural hairline wherever the group changes — the divider between the transform
    // run and the clipboard run (docs/DESIGN.md §4.4, §6.2). It is derived from the data, so
    // a regrouped registry redraws itself and no literal index like the legacy
    // `tabs.slice(0, 4)` can go stale.
    const separated = previous !== undefined && previous.group !== item.group;
    const rowBreak = separated && marksRowBreak && !rowBreakTaken;
    if (rowBreak) rowBreakTaken = true;

    const variant = item.variant ?? 'tinted';

    return (
      <Fragment key={item.id}>
        {separated ? (
          <div
            className={styles.sep}
            aria-hidden="true"
            {...(rowBreak ? { 'data-row-break': '' } : {})}
          />
        ) : null}
        <ToolbarButton
          // Layout belongs to the bar, not to the button: `Button` decides what a control
          // looks like, and these two classes decide how much of the dock's row it takes.
          className={
            variant === 'primary' ? `${styles.item} ${styles.primaryItem}` : (styles.item ?? '')
          }
          label={item.label}
          icon={item.icon}
          variant={variant}
          hue={item.hue}
          compactLabel={item.compactLabel}
          badge={item.badge}
          shortcutHint={item.shortcutHint}
          title={item.title}
          ariaLabel={item.ariaLabel}
          disabled={item.disabled}
          onInvoke={item.onInvoke}
        />
      </Fragment>
    );
  });
}

export function Toolbar({ items, label, documentTag, className }: ToolbarProps): ReactElement {
  const visible = items.filter((item) => item.available !== false);
  const leading = visible.filter((item) => item.align !== 'end');
  const trailing = visible.filter((item) => item.align === 'end');

  return (
    // `role="toolbar"` with every button its own tab stop, rather than the APG roving-focus
    // pattern: docs/DESIGN.md §7.4 fixes Tab traversal as visual order across the whole
    // product, and a toolbar that swallows five of six tab stops teaches a different rule in
    // one place only. The landmark still groups the actions for a screen reader.
    <div
      role="toolbar"
      aria-label={label}
      aria-orientation="horizontal"
      className={className === undefined ? styles.bar : `${styles.bar} ${className}`}
    >
      {/*
        Only the leading run can hold the dock's row break: `align: 'end'` items are rendered
        after the spacer, which is `display: none` on a phone, so a break marked there would
        wrap the dock in the wrong place.
      */}
      {renderRun(leading, true)}
      <div className={styles.spacer} />
      {/*
        Before the trailing run, not after it: the tag is the quietest thing in the bar and
        belongs against the spacer, with "Load example" still the far-right control
        (ADR-0101 §7.6).
      */}
      {documentTag === undefined ? null : <span className={styles.tag}>{documentTag}</span>}
      {renderRun(trailing, false)}
    </div>
  );
}

export default Toolbar;
